/* eslint-disable no-restricted-syntax */
/* eslint-disable no-console */

import _ from 'lodash';
import { ModelTreeNode, ModelTreeNodeDataFolder, ParsedTreeNodeType } from '../data/modelOptionsTree';
import { TreeManipulation } from '../logic/treeManipulation';
import { ModelRuleData, ModelRuleDomainGroupData } from './modelRuleData';
import { SLTXFormatRootFile } from '../../fileFormats/sltx/sltx';
import { ModelGroupInfo } from '../../fileFormats/sltx/modelGroupInfo';

export class RulesEvaluationDomains {

  public static evaluateRule(
    // The rule to evaluate
    domainsRule : ModelRuleData,
    // The tree to show the user
    modelTree : ModelTreeNode,
    // The details about the model
    sltxFile : SLTXFormatRootFile,
  ) : ModelTreeNode {

    if (domainsRule === undefined || domainsRule.ruleDataDomain === undefined) {
      return modelTree;
    }

    const { domainGroups } = domainsRule.ruleDataDomain;

    // If there are no conditions, return the original properties
    if (domainGroups.length === 0) {
      console.warn("There are no domain groups in the rule, so it won't adjust the tree");
      return modelTree;
    }

    const modelGroups = sltxFile.groups;
    if (modelGroups === undefined) {
      console.warn("There are no groups in the model, so it won't adjust the tree");
      return modelTree;
    }

    // This rule will have several domain groups.

    // Each domain group will have a set of groups defined.
    // Inside each group, zero or more options will be selected.

    // The rule will force us to create a new level in the tree,
    // that will contain all the groups referenced in the rule.

    // Depending on the current selection, we should also filter out any option
    // that does not belong to this domain.

    // Create a new tree to be returned

    const newModelTree = _.cloneDeep(modelTree);

    // Collect all the groups that are referenced in all the domain groups in the rule

    const allReferencedGroups = this.collectAllReferencedGroups(domainGroups);

    // Define a place to where the domain node should be inserted in the original tree

    let domainNodeInsertionPosition : {
      parent: ModelTreeNode,
      position: number,
    } | undefined;

    // Create a new tree node, which represents the domain.
    // This node will represent the Domain being processed here

    const newDomainTreeNode = new ModelTreeNode();
    newDomainTreeNode.tagTranslation = domainsRule.ruleDataDomain.tagTranslation;
    newDomainTreeNode.name = domainsRule.name || '';
    newDomainTreeNode.type = ParsedTreeNodeType.Domain;
    newDomainTreeNode.id = 0; // For now, let's leave this id as 0

    // Create new nodes to represent the domain groups in the rule
    // Go through each domain group, create a node to represent it, and add it to the domain node

    for (const domainGroup of domainsRule.ruleDataDomain.domainGroups) {

      // Create a new node to represent a domain group

      const newDomainGroupTreeNode = new ModelTreeNode();
      newDomainGroupTreeNode.tagTranslation = domainGroup.tagTranslation;
      newDomainGroupTreeNode.name = domainGroup.name || '';
      newDomainGroupTreeNode.type = ParsedTreeNodeType.DomainGroup;
      newDomainGroupTreeNode.id = domainGroup.id || 0;

      // Small hack for now to save this default thumbnail
      const catalogDomaingGroupData = new ModelTreeNodeDataFolder();
      // eslint-disable-next-line max-len
      catalogDomaingGroupData.thumbnailRef = RulesEvaluationDomains.editoResourcePath ? domainGroup.imagePath : domainGroup.thumbnailRef;
      newDomainGroupTreeNode.data = catalogDomaingGroupData;

      for (const modelGroup of modelGroups) {

        // Check if this group is referenced in the domain group

        const domainGroupConditionGroup = domainGroup.conditions.find(
          (groupCondition) => groupCondition.groupID === modelGroup.id,
        );

        if (domainGroupConditionGroup === undefined) {

          // This group is not referenced in the domain group
          // eslint-disable-next-line no-continue
          continue;
        }

        // Grab the groupID

        const relevantGroupID = domainGroupConditionGroup.groupID;

        // Find the node in the tree, that is associated with this group

        const domainGroupGroupNode = TreeManipulation.findNodeInTree(
          modelTree,
          undefined,
          undefined,
          (node) => node.type === ParsedTreeNodeType.Group
              && node.id === relevantGroupID,
        );

        if (domainGroupGroupNode.node === undefined) {
          console.warn(`TreeManipulation.findNodeInTree failed with (${relevantGroupID}/Group)`);
          // eslint-disable-next-line no-continue
          continue;
        }

        // Check if the position we found this group would be good to drop the domain node

        if (domainNodeInsertionPosition === undefined
          && domainGroupGroupNode.parent !== undefined
          && domainGroupGroupNode.indexInParent !== undefined) {

          domainNodeInsertionPosition = {
            parent: domainGroupGroupNode.parent,
            position: domainGroupGroupNode.indexInParent,
          };
        }

        // If the node is found, make a copy

        const groupNode = _.cloneDeep(domainGroupGroupNode.node);

        // Find the group in the model

        const group = modelGroups.find((g) => g.id === relevantGroupID);
        if (group === undefined) {
          console.warn(`Could not find group in the model with id ${relevantGroupID}`);
          // eslint-disable-next-line no-continue
          continue;
        }

        // Not all the options in the node are to be included in the domain group.
        // We shall remove the ones not present in the domain group

        const groupOptions = ModelGroupInfo.getGroupAllOptions(group);
        for (const groupOption of groupOptions) {

          // Check if this option is selected

          const optionIsIncludedInDomainGroup = domainGroupConditionGroup.options.includes(
            groupOption.id,
          );

          // If the option is not included, we shall remove it from the groupNode

          if (optionIsIncludedInDomainGroup === false) {
            TreeManipulation.removeNodeFromTree(
              groupNode,
              (node) => node.type === ParsedTreeNodeType.Option
                  && node.id === groupOption.id,
            );
          }
        }

        // Add the adjusted tree to the domain group tree node
        newDomainGroupTreeNode.children.push(groupNode);
      }

      // Add these nodes to the domainNode

      newDomainTreeNode.children.push(newDomainGroupTreeNode);
    }

    // Remove all the mentioned groups from the original tree

    for (const referencedGroupID of allReferencedGroups) {
      TreeManipulation.removeNodeFromTree(
        newModelTree,
        (node) => node.type === ParsedTreeNodeType.Group
            && node.id === referencedGroupID,
      );
    }

    // Check if the position was defined
    if (domainNodeInsertionPosition === undefined) {

      // Simply add if to the end of the root node
      newModelTree.children.push(newDomainTreeNode);
    } else {
      newModelTree.children.splice(
        domainNodeInsertionPosition.position,
        0,
        newDomainTreeNode,
      );
    }

    TreeManipulation.trimEmptyContainers(newModelTree);
    TreeManipulation.removeSingleFoldersFromGroups(newModelTree);

    return newModelTree;
  }

  private static collectAllReferencedGroups(
    domainGroups : ModelRuleDomainGroupData[],
  ) : number[] {

    // We need to go through all the domain groups, and collect all the groups
    // that are referenced in the rule.
    // We will use a Set to avoid duplicates.
    const allReferencedGroups = new Set<number>();

    domainGroups.forEach((domainGroup) => {
      domainGroup.conditions.forEach((group) => {
        allReferencedGroups.add(group.groupID || 0);
      });
    });

    return Array.from(allReferencedGroups);
  }

  public static editoResourcePath: boolean = false;
}
